import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { coachThreadClosedReason } from './kaiCoachErrors';
import type { CoachBlock, CoachMessage, CoachThreadKey } from './kaiCoachService';
import { useKaiCoachService } from './useKaiCoachService';

interface UseKaiCoachConversationParams {
  activityUid: string;
  courseId: string;
  moduleId: string;
  sessionUid: string;
  // null while no thread is selected (e.g. all sessions still locked).
  enabled: boolean;
  // When true, a successful send invalidates the loaded course component so the
  // student's participation score (stored in section_stats and written by the
  // backend during the send) refreshes without a manual page reload.
  refreshComponentOnSend?: boolean;
}

export interface KaiCoachConversation {
  error: string | null;
  injectedBlocks: CoachBlock[];
  // True once the server has refused a message because the thread's due date
  // has passed. The view closes the thread on this even when its own clock
  // still says open, and shows the closed state rather than an error; it
  // resets when the thread changes.
  isClosedByServer: boolean;
  // Goal-reached credit: true once the thread's goal is reached — the loaded
  // conversation records it, a message carries the goal badge (so the awarding
  // reply completes the thread at once), or the server refused a send as
  // complete. The view then withdraws the composer; it resets per thread.
  isGoalReached: boolean;
  isLoading: boolean;
  isSending: boolean;
  messages: CoachMessage[];
  sendMessage: (text: string, focusAreaUid?: string) => Promise<void>;
}

const GENERIC_ERROR =
  'Something went wrong reaching your coach. Please try again in a moment.';
const NOT_OPEN_ERROR =
  'This session hasn’t opened yet, so Kai can’t take messages until it does.';

export function useKaiCoachConversation({
  activityUid,
  courseId,
  enabled,
  moduleId,
  refreshComponentOnSend = false,
  sessionUid,
}: UseKaiCoachConversationParams): KaiCoachConversation {
  const queryClient = useQueryClient();
  const { service, userId } = useKaiCoachService();

  const threadKey = useMemo<CoachThreadKey | null>(() => {
    if (!enabled || !service || !userId || !courseId || !moduleId) {
      return null;
    }
    return { activityUid, courseUid: courseId, moduleUid: moduleId, sessionUid, userId };
  }, [activityUid, courseId, enabled, moduleId, service, sessionUid, userId]);

  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [injectedBlocks, setInjectedBlocks] = useState<CoachBlock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosedByServer, setIsClosedByServer] = useState(false);
  const [goalReachedAtLoad, setGoalReachedAtLoad] = useState(false);
  const [isCompleteByServer, setIsCompleteByServer] = useState(false);

  // Guards against out-of-order responses when the selected thread changes fast.
  const requestIdRef = useRef(0);

  useEffect(() => {
    setIsClosedByServer(false);
    setGoalReachedAtLoad(false);
    setIsCompleteByServer(false);
    if (!service || !threadKey) {
      setMessages([]);
      setInjectedBlocks([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    service
      .loadConversation(threadKey)
      .then((result) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setMessages(result.messages);
        setInjectedBlocks(result.injectedBlocks);
        setGoalReachedAtLoad(typeof result.goalReachedAtMillis === 'number');
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(GENERIC_ERROR);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [service, threadKey]);

  const sendMessage = useCallback(
    async (text: string, focusAreaUid = '') => {
      const trimmed = text.trim();
      if (!service || !threadKey || !trimmed || isSending) {
        return;
      }
      const optimistic: CoachMessage = {
        blocks: [{ markdown: trimmed, type: 'markdown' }],
        id: `pending-${Date.now()}`,
        role: 'user',
      };
      setMessages((previous) => [...previous, optimistic]);
      setIsSending(true);
      setError(null);
      try {
        const assistant = await service.sendMessage(threadKey, trimmed, focusAreaUid);
        setMessages((previous) => [...previous, assistant]);
        // The backend may have just updated the student's participation score
        // (in the course component's section_stats). Refresh the loaded component
        // so the displayed scores reflect it without a manual page reload.
        if (refreshComponentOnSend) {
          void queryClient.invalidateQueries({
            queryKey: ['component', courseId, moduleId],
          });
        }
      } catch (caught) {
        const reason = coachThreadClosedReason(caught);
        if (reason !== null) {
          // The server stored nothing, so the optimistic turn comes back out:
          // leaving it would read as a message sent before the deadline.
          setMessages((previous) =>
            previous.filter((message) => message.id !== optimistic.id),
          );
        }
        if (reason === 'closed') {
          // Not an error to report: the view shows the closed state instead,
          // which says everything a message here would.
          setIsClosedByServer(true);
        } else if (reason === 'complete') {
          // Likewise: the goal was reached (in another tab, say); the view
          // shows the thread as complete.
          setIsCompleteByServer(true);
        } else if (reason === 'notOpen') {
          setError(NOT_OPEN_ERROR);
        } else {
          setError(GENERIC_ERROR);
        }
      } finally {
        setIsSending(false);
      }
    },
    [courseId, isSending, moduleId, queryClient, refreshComponentOnSend, service, threadKey],
  );

  const hasGoalBadge = useMemo(
    () => messages.some((message) => message.blocks.some((block) => block.type === 'goalReached')),
    [messages],
  );

  return {
    error,
    injectedBlocks,
    isClosedByServer,
    isGoalReached: goalReachedAtLoad || hasGoalBadge || isCompleteByServer,
    isLoading,
    isSending,
    messages,
    sendMessage,
  };
}
